import { ReactNode, useEffect, useState } from 'react';
import { prefersReducedMotion } from '../../../utils/prefersReducedMotion';
import styles from './CollapsibleBody.module.scss';

interface CollapsibleBodyProps {
    isOpen: boolean;
    drawerMountedAt: number;
    /** false renders an initially-open body straight into its open state -- for a body nested
     *  inside another CollapsibleBody, whose own opening already animates it in. */
    animateInitialOpen?: boolean;
    bodyClassName?: string;
    children: ReactNode;
}

/*
    Animated disclosure for a collapsible section.

    The body mounts on first open and then stays mounted: unmounting on toggle would leave
    nothing for CSS to transition, and mounting up front would render a large collapsed body
    before anyone asked for it.

    The collapse rides on a grid track (0fr -> 1fr) rather than a max-height, because the body's
    height is unknown; a guessed max-height either clips it or spends the easing on empty space.
    Browsers that cannot interpolate a grid track open instantly, a clean degradation.

    `visibility` transitions alongside, so focusable content inside a collapsed body leaves the
    tab order and accessibility tree once collapsing finishes, and returns immediately on open.

    SideDrawer's entrance runs 500ms; a body expanding inside that window finishes behind a panel
    the user cannot see yet and just looks open on arrival. So the initial expansion waits for the
    drawer to settle; later toggles are immediate.
*/
const DRAWER_ENTRANCE_MS = 500;
/* A short beat after the panel lands, so the two motions read as sequential, not simultaneous. */
const AFTER_ENTRANCE_BEAT_MS = 80;

const CollapsibleBody = ({ isOpen, drawerMountedAt, animateInitialOpen = true, bodyClassName, children }: CollapsibleBodyProps) => {
    const [mounted, setMounted] = useState(isOpen);
    /*
        Starts closed even when the section is already open on mount (unless the caller opts out
        with animateInitialOpen). A section that opens with the drawer would otherwise render
        straight into its end state and just appear; the effect below opens it on a later tick.
    */
    const [open, setOpen] = useState(isOpen && !animateInitialOpen);

    useEffect(() => {
        if (isOpen && !mounted) setMounted(true);
    }, [isOpen, mounted]);

    useEffect(() => {
        if (!isOpen) {
            setOpen(false);
            return undefined;
        }
        if (!mounted || open) return undefined;
        if (prefersReducedMotion()) {
            // No transition to stage, so there is nothing to wait for behind the drawer's entrance.
            setOpen(true);
            return undefined;
        }

        /*
            Mounting and opening within one frame would start the transition already at its end
            state, so the section would snap. Applying the open class on a later tick gives the
            browser a painted 0fr to animate away from.
        */
        const settleIn = DRAWER_ENTRANCE_MS + AFTER_ENTRANCE_BEAT_MS - (performance.now() - drawerMountedAt);
        if (settleIn <= 0) {
            // The drawer is already sitting still — this is a user toggle, so open immediately.
            const frame = requestAnimationFrame(() => setOpen(true));
            return () => cancelAnimationFrame(frame);
        }
        const timer = setTimeout(() => setOpen(true), settleIn);
        return () => clearTimeout(timer);
    }, [isOpen, mounted, open, drawerMountedAt]);

    if (!mounted) return null;

    /*
        Three nested elements, each load-bearing:

        - the wrap animates `grid-template-rows: 0fr -> 1fr`, so it collapses to the body's own
          natural height, whatever its content. A max-height cannot, because it would have to
          guess that height.
        - the clip is separate and deliberately padding-free. Padding survives a zero-height
          content box, so carrying it here would leave a sliver of layout under a closed
          section's header.
        - the body carries all the padding.

        The 320ms in the stylesheet is the measured duration: shorter felt rushed, longer felt sluggish.
    */
    return (
        <div className={open ? styles.WrapOpen : styles.Wrap}>
            <div className={styles.Clip}>
                <div className={bodyClassName}>{children}</div>
            </div>
        </div>
    );
};

export default CollapsibleBody;
